﻿import { ensureJQuery, ensureParsley, ensureInputmask } from "@js/core/loaders/vendors";
import { onlyDigits } from "@js/core/utils/text";
import { parseTrCurrency } from "@js/core/utils/number";
import { initCheckoutMasks } from "@js/core/forms/inputmask-checkout";
import { registerCheckoutValidators, initCheckoutParsley } from "@js/core/forms/parsley-checkout";
import { initSessionCountdown } from "@js/core/ui/session-countdown";
import { getJson, postHtml } from "@js/core/http/fetch";
import { t, currentLang } from "@js/core/utils/i18n";

function getEl(selector) {
    return document.querySelector(selector);
}

function getEls(selector) {
    return document.querySelectorAll(selector);
}

function show(el) {
    if (el) el.style.display = "";
}

function hide(el) {
    if (el) el.style.display = "none";
}

function setText(selector, value) {
    const el = getEl(selector);
    if (el) el.textContent = value;
}

function clearBrandImage(img) {
    if (!img) return;

    img.classList.remove("is-ready", "is-error");
    img.classList.add("is-loading");
    img.style.display = "none";
    img.removeAttribute("src");
}

function loadBrandImage(selector, src, network) {
    const imgEl = getEl(selector);
    if (!imgEl) return;

    clearBrandImage(imgEl);

    if (!src) return;

    const img = new Image();

    img.onload = () => {
        imgEl.setAttribute("src", src);
        imgEl.style.display = "";

        void imgEl.offsetWidth;

        imgEl.classList.remove("is-loading", "is-error");
        imgEl.classList.add("is-ready");
    };

    img.onerror = () => {
        imgEl.classList.remove("is-loading", "is-ready");
        imgEl.classList.add("is-error");
        imgEl.style.display = "none";
        imgEl.removeAttribute("src");
    };

    img.src = src;
    if (network) {
        imgEl.setAttribute("data-network", network);
    }
}

function fillBinInfo(data) {
    console.log(data);
    loadBrandImage("#cardNetworkImage", data?.NetworkImage, data?.Network);
    loadBrandImage("#cardGroupImage", data?.CardGroupImage, '');
    loadBrandImage("#cardBankImage", data?.BankImage, '');
}

function resetBinInfo() {
    clearBrandImage(getEl("#cardNetworkImage"));
    clearBrandImage(getEl("#cardGroupImage"));
    clearBrandImage(getEl("#cardBankImage"));

    setText("#cardBinBankName", "-");

    const network = getEl("#cardBinNetwork");
    if (network) {
        network.classList.remove("is-visa", "is-mastercard", "is-troy");
        network.textContent = "-";
    }

    setText("#cardBinType", "-");
    setText("#cardBinProduct", "-");
    setText("#cardBinInstallment", "-");

    hide(getEl("#cardBinResult"));
}

function createInstallmentManager() {
    let installmentAbortController = null;
    let installmentRequestKey = null;
    let installmentRequestToken = 0;
    let onStateChanged = null;

    const loading = getEl("#installmentLoading");
    const empty = getEl("#installmentEmpty");
    const table = getEl("#installmentTable");
    const tableBody = getEl("#installmentTableBody");
    const selectedInstallment = getEl("#SelectedInstallment");
    const submitBtn = getEl("#paymentCheckoutSubmitBtn");

    function notifyStateChanged() {
        if (typeof onStateChanged === "function") {
            onStateChanged();
        }
    }

    function setOnStateChanged(callback) {
        onStateChanged = callback;
    }

    function hasInstallment() {
        return getEls("#installmentTableBody .installment-row--item:not(.is-skeleton)").length > 0 ||
            getEls('#installmentTableBody input[type="radio"][name="installment"]').length > 0;
    }

    function setSubmitEnabled(enabled) {
        if (!submitBtn) return;

        if (enabled) {
            submitBtn.disabled = false;
            submitBtn.classList.remove("is-disabled");
        } else {
            submitBtn.disabled = true;
            submitBtn.classList.add("is-disabled");
            submitBtn.classList.remove("is-loading");
        }
    }

    function createSkeletonRow(index) {
        const row = document.createElement("div");
        row.className = "installment-row installment-row--item is-skeleton";
        row.style.setProperty("--skeleton-delay", `${Math.min(index * 45, 360)}ms`);

        row.innerHTML = `
            <div class="col-radio">
                <span class="skeleton skeleton--radio"></span>
            </div>
            <div class="col-count">
                <span class="skeleton skeleton--sm skeleton--full"></span>
            </div>
            <div class="col-rate">
                <span class="skeleton skeleton--md skeleton--full monospace"></span>
            </div>
            <div class="col-total">
                <span class="skeleton skeleton--lg skeleton--full monospace"></span>
            </div>
            <div class="col-base">
                <span class="skeleton skeleton--lg skeleton--full monospace"></span>
            </div>
            <div class="col-pay">
                <span class="skeleton skeleton--xl skeleton--full"></span>
            </div>
        `;

        return row;
    }

    function renderSkeletonRows(count = 12) {
        if (!tableBody) return;

        tableBody.innerHTML = "";

        for (let i = 0; i < count; i++) {
            tableBody.appendChild(createSkeletonRow(i));
        }
    }

    function convertRowsToSkeleton() {
        if (!tableBody) return;

        const rows = tableBody.querySelectorAll(".installment-row--item:not(.is-skeleton)");

        rows.forEach((row, index) => {
            row.classList.add("is-installment-loading");
            row.style.setProperty("--skeleton-delay", `${Math.min(index * 35, 280)}ms`);

            row.querySelectorAll(".col-rate, .col-total, .col-base, .col-pay").forEach((cell) => {
                cell.classList.add("is-skeleton-cell");
            });
        });
    }

    function clearSkeletonState() {
        if (!tableBody) return;

        tableBody.querySelectorAll(".is-installment-loading").forEach((row) => {
            row.classList.remove("is-installment-loading");
        });

        tableBody.querySelectorAll(".is-skeleton-cell").forEach((cell) => {
            cell.classList.remove("is-skeleton-cell");
        });
    }

    function parseInstallmentHtml(html) {
        const temp = document.createElement("div");
        temp.innerHTML = html;

        return Array.from(temp.querySelectorAll(".installment-row--item"));
    }

    function getRowKey(row) {
        const radio = row.querySelector('input[type="radio"][name="installment"]');
        return radio?.value || row.dataset.installment || row.querySelector(".col-count")?.textContent?.trim() || row.textContent.trim();
    }

    function animateInsertRows(container, newRows) {
        container.innerHTML = "";

        newRows.forEach((row, index) => {
            row.classList.add("is-installment-entering");
            row.style.setProperty("--enter-delay", `${Math.min(index * 65, 520)}ms`);

            container.appendChild(row);

            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    row.classList.remove("is-installment-entering");
                    row.classList.add("is-installment-entered");

                    window.setTimeout(() => {
                        row.classList.remove("is-installment-entered");
                        row.style.removeProperty("--enter-delay");
                    }, 760);
                });
            });
        });
    }

    function animateUpdatedCell(cell) {
        cell.classList.remove("is-installment-cell-updated");
        void cell.offsetWidth;
        cell.classList.add("is-installment-cell-updated");

        window.setTimeout(() => {
            cell.classList.remove("is-installment-cell-updated");
        }, 820);
    }

    function animateUpdatedRow(row) {
        row.classList.remove("is-installment-row-updated");
        void row.offsetWidth;
        row.classList.add("is-installment-row-updated");

        window.setTimeout(() => {
            row.classList.remove("is-installment-row-updated");
        }, 860);
    }

    function updateRowValues(currentRow, newRow) {
        const selectors = [
            ".col-count",
            ".col-rate",
            ".col-total",
            ".col-base",
            ".col-pay"
        ];

        let changed = false;

        selectors.forEach((selector) => {
            const currentCell = currentRow.querySelector(selector);
            const newCell = newRow.querySelector(selector);

            if (!currentCell || !newCell) return;

            const currentHtml = currentCell.innerHTML.trim();
            const newHtml = newCell.innerHTML.trim();

            if (currentHtml !== newHtml) {
                currentCell.innerHTML = newHtml;
                animateUpdatedCell(currentCell);
                changed = true;
            }
        });

        const currentRadio = currentRow.querySelector('input[type="radio"][name="installment"]');
        const newRadio = newRow.querySelector('input[type="radio"][name="installment"]');

        if (currentRadio && newRadio) {
            currentRadio.value = newRadio.value;
            currentRadio.checked = newRadio.checked;
        }

        if (changed) {
            animateUpdatedRow(currentRow);
        }
    }

    function animateUpdateRows(container, newRows) {
        const currentRows = Array.from(container.querySelectorAll(".installment-row--item:not(.is-skeleton)"));

        const currentByKey = new Map();
        currentRows.forEach((row) => {
            currentByKey.set(getRowKey(row), row);
        });

        newRows.forEach((newRow) => {
            const key = getRowKey(newRow);
            const currentRow = currentByKey.get(key);

            if (currentRow) {
                updateRowValues(currentRow, newRow);
            }
        });
    }

    function showInstallmentLoading() {
        hide(loading);
        hide(empty);

        if (tableBody) {
            const hasRealRows = tableBody.querySelector(".installment-row--item:not(.is-skeleton)");

            if (hasRealRows) {
                convertRowsToSkeleton();
            } else {
                renderSkeletonRows(12);
            }
        }

        show(table);
        setSubmitEnabled(false);
        notifyStateChanged();
    }

    function showInstallmentEmpty(message) {
        hide(loading);
        hide(table);

        if (tableBody) tableBody.innerHTML = "";

        if (empty) {
            empty.textContent = message || t("Installments.Hint");
            show(empty);
        }

        if (selectedInstallment) {
            selectedInstallment.value = "1";
        }

        setSubmitEnabled(false);
        notifyStateChanged();
    }

    function bindInstallmentSelection() {
        const radios = getEls('#installmentTableBody .installment-row--item input[type="radio"][name="installment"], #installmentTableBody input[type="radio"][name="installment"]');

        radios.forEach((radio) => {
            radio.addEventListener("change", () => {
                getEls("#installmentTableBody .installment-row--item").forEach((row) => {
                    row.classList.remove("is-selected");
                });

                const row = radio.closest(".installment-row--item");
                if (row) row.classList.add("is-selected");

                if (selectedInstallment) {
                    selectedInstallment.value = radio.value;
                }

                notifyStateChanged();
            });
        });
    }

    function restoreSelectedInstallment() {
        if (!tableBody || !selectedInstallment) return;

        const wantedValue = selectedInstallment.value || "1";
        const radios = Array.from(
            tableBody.querySelectorAll('input[type="radio"][name="installment"]')
        );

        if (!radios.length) {
            selectedInstallment.value = "1";
            return;
        }

        const matched = radios.find((radio) => radio.value === wantedValue);

        const radioToCheck = matched || radios[0];

        radioToCheck.checked = true;
        radioToCheck.dispatchEvent(new Event("change", { bubbles: true }));
    }

    function showInstallmentTable(html) {
        hide(loading);
        hide(empty);

        if (!tableBody) return;

        const newRows = parseInstallmentHtml(html);
        const currentRows = Array.from(tableBody.querySelectorAll(".installment-row--item:not(.is-skeleton)"));
        const hasSkeletonRows = tableBody.querySelector(".installment-row--item.is-skeleton");
        const sameCount = currentRows.length === newRows.length && currentRows.length > 0;

        clearSkeletonState();

        if (sameCount && !hasSkeletonRows) {
            animateUpdateRows(tableBody, newRows);
        } else {
            animateInsertRows(tableBody, newRows);
        }

        show(table);

        window.setTimeout(() => {
            bindInstallmentSelection();
            restoreSelectedInstallment();
            notifyStateChanged();
        }, sameCount ? 40 : 120);
    }

    async function fetchInstallmentsIfReady() {
        const cardDigits = onlyDigits(getEl("#CardNumber")?.value || "");
        const amount = parseTrCurrency(getEl("#Amount")?.value || "");

        setSubmitEnabled(false);

        if (cardDigits.length < 8 || amount <= 0) {
            installmentRequestKey = null;

            if (installmentAbortController) {
                installmentAbortController.abort();
            }

            showInstallmentEmpty(t("Installments.Hint"));
            return;
        }

        const binCode = cardDigits.substring(0, 8);
        const requestKey = `${binCode}|${amount}`;

        if (installmentRequestKey === requestKey) {
            notifyStateChanged();
            return;
        }

        installmentRequestKey = requestKey;
        installmentRequestToken++;

        const currentToken = installmentRequestToken;

        if (installmentAbortController) {
            installmentAbortController.abort();
        }

        installmentAbortController = new AbortController();

        showInstallmentLoading();

        try {
            const html = await postHtml(`/sanal-pos/installments/${binCode}`, { amount });

            if (currentToken !== installmentRequestToken) return;

            const trimmed = (html || "").trim();

            if (!trimmed) {
                showInstallmentEmpty(t("Installments.None"));
                return;
            }

            showInstallmentTable(trimmed);
        } catch (error) {
            if (installmentAbortController?.signal?.aborted) return;
            if (currentToken !== installmentRequestToken) return;

            showInstallmentEmpty(t("Installments.Error"));
        }
    }

    return {
        setSubmitEnabled,
        showInstallmentEmpty,
        fetchInstallmentsIfReady,
        hasInstallment,
        setOnStateChanged
    };
}

function createFormStateManager(form, installmentManager, parsleyInstance) {
    const submitBtn = getEl("#paymentCheckoutSubmitBtn");

    function isFormValid(silent = true) {
        if (!form) return false;

        if (silent) {
            const requiredEmpty = Array.from(form.querySelectorAll("[data-parsley-required='true'], [required]"))
                .some((el) => {
                    if (el.type === "checkbox") return !el.checked;
                    return !String(el.value || "").trim();
                });

            if (requiredEmpty) return false;

            return parsleyInstance ? parsleyInstance.isValid({ force: false }) : false;
        }

        return parsleyInstance ? parsleyInstance.isValid() : false;
    }

    function update() {
        const valid = isFormValid(true);
        const hasInstallment = installmentManager.hasInstallment();

        installmentManager.setSubmitEnabled(valid && hasInstallment);
    }

    function bind() {
        if (!form) return;

        const handler = () => {
            window.requestAnimationFrame(update);
        };

        form.addEventListener("input", handler, true);
        form.addEventListener("change", handler, true);
        form.addEventListener("blur", handler, true);
        form.addEventListener("keyup", handler, true);

        if (window.jQuery) {
            const $ = window.jQuery;

            $(form).on("field:validated form:validated", handler);
            $(form).find("input, select, textarea").on("input keyup change blur", handler);
        }

        update();
    }

    function lockForSubmit() {
        if (!submitBtn) return;

        submitBtn.classList.add("is-loading");
        submitBtn.disabled = true;
    }

    return {
        bind,
        update,
        isFormValid,
        lockForSubmit
    };
}

function createBinLookupManager() {
    let lastBinCode = null;
    let currentAbortController = null;

    async function lookupBin(binCode) {
        if (!binCode || binCode.length < 6) {
            lastBinCode = null;
            resetBinInfo();
            return;
        }

        if (lastBinCode === binCode) return;
        lastBinCode = binCode;

        if (currentAbortController) {
            currentAbortController.abort();
        }

        currentAbortController = new AbortController();

        try {
            const resp = await getJson(`/sanal-pos/bincode/${binCode}`, {
                signal: currentAbortController.signal
            });

            if (!resp) {
                resetBinInfo();
                return;
            }

            fillBinInfo(resp);
        } catch (_) {
            if (currentAbortController?.signal?.aborted) return;
            resetBinInfo();
        }
    }

    function bind() {
        const cardNumber = getEl("#CardNumber");
        if (!cardNumber) return;

        const handleLookup = () => {
            const digits = onlyDigits(cardNumber.value);

            if (digits.length >= 6) {
                lookupBin(digits.substring(0, 6));
            } else {
                resetBinInfo();
                lastBinCode = null;
            }
        };

        cardNumber.addEventListener("input", handleLookup);
        cardNumber.addEventListener("keyup", handleLookup);
        cardNumber.addEventListener("change", handleLookup);
        cardNumber.addEventListener("blur", handleLookup);

        if (window.jQuery) {
            window.jQuery(cardNumber).on("input keyup change blur", handleLookup);
        }
    }

    function triggerInitial() {
        const cardNumber = getEl("#CardNumber");
        if (!cardNumber) return;

        const digits = onlyDigits(cardNumber.value);

        if (digits.length >= 6) {
            lookupBin(digits.substring(0, 6));
        }
    }

    return { bind, triggerInitial };
}

function bindInstallmentTriggers(installmentManager, formStateManager) {
    const cardNumber = getEl("#CardNumber");
    const amount = getEl("#Amount");

    const run = () => {
        installmentManager.fetchInstallmentsIfReady().finally(() => {
            formStateManager.update();
        });
    };

    if (cardNumber) {
        cardNumber.addEventListener("input", run);
        cardNumber.addEventListener("keyup", run);
        cardNumber.addEventListener("change", run);
        cardNumber.addEventListener("blur", run);

        if (window.jQuery) {
            window.jQuery(cardNumber).on("input keyup change blur", run);
        }
    }

    if (amount) {
        amount.addEventListener("input", run);
        amount.addEventListener("keyup", run);
        amount.addEventListener("change", run);
        amount.addEventListener("blur", run);

        if (window.jQuery) {
            window.jQuery(amount).on("input keyup change blur", run);
        }
    }
}

function showPaymentWaitOverlay() {
    const overlay = getEl("#paymentWaitOverlay");
    if (!overlay) return;

    overlay.classList.add("is-visible");
    overlay.setAttribute("aria-hidden", "false");
    document.documentElement.classList.add("is-payment-waiting");
}

function bindSubmitState(formStateManager) {
    if (!window.jQuery) return;

    const $ = window.jQuery;
    const $form = $("#paymentCheckoutForm");
    const $submit = $("#paymentCheckoutSubmitBtn");

    if (!$form.length || !$submit.length) return;

    $form.off("submit.virtualPosCheckout").on("submit.virtualPosCheckout", function (e) {
        const parsley = $form.parsley();

        if (!parsley.isValid()) {
            e.preventDefault();
            parsley.validate();
            formStateManager.update();
            return false;
        }

        if ($submit.prop("disabled")) {
            e.preventDefault();
            formStateManager.update();
            return false;
        }

        formStateManager.lockForSubmit();
        showPaymentWaitOverlay();
        return true;
    });
}

export async function initVirtualPosCheckout() {
    const form = getEl("#paymentCheckoutForm");
    if (!form) return;

    await ensureJQuery();
    await ensureInputmask();
    await ensureParsley(currentLang());

    initCheckoutMasks();
    triggerInitialFieldEvents();

    registerCheckoutValidators();

    const parsley = initCheckoutParsley("#paymentCheckoutForm");

    const expireSecondsRaw = document.body?.dataset?.expireSeconds || "0";
    const expireSeconds = parseInt(expireSecondsRaw, 10);

    if (Number.isFinite(expireSeconds) && expireSeconds > 0) {
        initSessionCountdown({ totalSeconds: expireSeconds });
    }

    const binLookupManager = createBinLookupManager();
    binLookupManager.bind();
    binLookupManager.triggerInitial();

    const installmentManager = createInstallmentManager();

    const formStateManager = createFormStateManager(form, installmentManager, parsley);
    installmentManager.setOnStateChanged(() => formStateManager.update());

    bindInstallmentTriggers(installmentManager, formStateManager);
    bindSubmitState(formStateManager);

    formStateManager.bind();

    await hydrateCheckoutFromExistingValues(installmentManager, formStateManager);
}

async function hydrateCheckoutFromExistingValues(installmentManager, formStateManager) {
    const cardDigits = onlyDigits(getEl("#CardNumber")?.value || "");
    const amount = parseTrCurrency(getEl("#Amount")?.value || "");

    if (cardDigits.length >= 8 && amount > 0) {
        await installmentManager.fetchInstallmentsIfReady();
        formStateManager.update();
        return;
    }

    installmentManager.showInstallmentEmpty(
        t("Installments.Hint")
    );

    formStateManager.update();
}

function triggerInitialFieldEvents() {
    const cardDigits = onlyDigits(getEl("#CardNumber")?.value || "");
    const amount = parseTrCurrency(getEl("#Amount")?.value || "");

    if (cardDigits.length < 8 && amount <= 0) return;

    ["#CardNumber", "#Amount", "#ExpireDate", "#Cvv", "#CardHolder"].forEach((selector) => {
        const el = getEl(selector);
        if (!el) return;

        el.dispatchEvent(new Event("input", { bubbles: true }));
        el.dispatchEvent(new Event("change", { bubbles: true }));

        if (window.jQuery) {
            window.jQuery(el).trigger("input").trigger("change");
        }
    });
}